import React, {
  PropsWithChildren,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { IconButton, Stack, Tooltip } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TagColumn, TagHeading, CountBadge, TagListBody } from "./styled";

interface TagListColumnProps {
  title: string;
  count: number;
  actions?: ReactNode; // optional right side header actions
  collapsible?: boolean; // enables collapse toggle
  defaultCollapsed?: boolean; // initial collapsed state
  height?: number | string; // override uniform height via CSS var
  "data-testid"?: string;
}

export const TagListColumn: React.FC<PropsWithChildren<TagListColumnProps>> = ({
  title,
  count,
  actions,
  collapsible = false,
  defaultCollapsed = false,
  height,
  children,
  "data-testid": dataTestId,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const columnTestId = dataTestId || "tag-list-column";
  const toggle = useCallback(
    () => collapsible && setCollapsed((c) => !c),
    [collapsible]
  );

  return (
    <TagColumn
      data-testid={columnTestId}
      style={
        height
          ? {
              ["--tag-column-height" as any]:
                typeof height === "number" ? `${height}px` : height,
            }
          : undefined
      }
      className={collapsed ? "collapsed" : undefined}
    >
      <TagHeading
        style={{ justifyContent: "space-between" }}
        data-testid={`${columnTestId}-header`}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          {collapsible && (
            <Tooltip
              title={collapsed ? "Expand" : "Collapse"}
              placement="top"
              arrow
            >
              <IconButton
                size="small"
                onClick={toggle}
                edge="start"
                sx={{ mr: 0.5 }}
                data-testid={`${columnTestId}-toggle`}
              >
                {collapsed ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ExpandLessIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          <span
            style={{
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            data-testid={`${columnTestId}-title`}
          >
            {title}
          </span>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {actions}
          <CountBadge data-testid={`${columnTestId}-count`}>{count}</CountBadge>
        </Stack>
      </TagHeading>
      {!collapsed && (
        <TagListBody data-testid={`${columnTestId}-body`}>{children}</TagListBody>
      )}
    </TagColumn>
  );
};

export default TagListColumn;
